import type { TRecordType } from '@moneyforward/ap-frontend-components';
import { isValidElement, type ReactNode } from 'react';
import type { CardColumn } from './type';

/**
 * Structural stand-in for rc-table's `RenderedCell`, which is not a direct
 * dependency here. Only `children` is ever read, so the full type is not needed.
 */
type RenderedCellLike = {
  children?: ReactNode;
  props?: object;
};

/**
 * Resolves antd's `dataIndex` (string | number | array) to a value.
 */
export const getRecordValue = <RecordType extends TRecordType>(
  record: RecordType,
  dataIndex: CardColumn<RecordType>['dataIndex']
): unknown => {
  if (dataIndex === undefined || dataIndex === null) return undefined;
  const paths = Array.isArray(dataIndex) ? dataIndex : [dataIndex];
  return paths.reduce<unknown>((acc, path) => {
    if (acc === null || acc === undefined) return undefined;
    if (typeof acc !== 'object') return undefined;
    return Reflect.get(acc, path);
  }, record);
};

/**
 * Narrows a raw value from a column without `render` to something renderable.
 * Values that cannot be rendered (plain objects) become null — those columns
 * need an explicit `render`.
 */
const toReactNode = (value: unknown): ReactNode => {
  if (value === null || value === undefined) return null;
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return value;
  }
  if (isValidElement(value)) return value;
  return null;
};

const isRenderedCell = (value: object): value is RenderedCellLike =>
  !isValidElement(value) &&
  !Array.isArray(value) &&
  ('children' in value || 'props' in value);

/**
 * Prefers the column's `render`, falling back to the raw value.
 * When `render` returns the `{ children, props }` form (antd's colSpan/rowSpan
 * control) only `children` is used — cards have no concept of merged cells.
 * `cardRender`, when set, wins over both — it is how a column supplies a
 * card-specific value when `render`'s table output differs.
 */
export const renderCellContent = <RecordType extends TRecordType>(
  column: CardColumn<RecordType>,
  record: RecordType,
  index: number
): ReactNode => {
  if (column.cardRender) return column.cardRender(record, index);
  const value = getRecordValue(record, column.dataIndex);
  if (!column.render) return toReactNode(value);
  const rendered = column.render(value, record, index);
  if (rendered !== null && typeof rendered === 'object') {
    if (isRenderedCell(rendered)) return rendered.children;
  }
  return rendered;
};

/**
 * antd's `title` can also be a function (used for sorters), but cards have no
 * sort UI, so only the directly renderable form is handled. `cardLabel`, when
 * set, takes precedence — it is how a column supplies a raw label for cards
 * when `title` is a function.
 */
export const renderColumnTitle = <RecordType extends TRecordType>(
  column: CardColumn<RecordType>
): ReactNode => {
  if (column.cardLabel !== undefined) return column.cardLabel;
  const { title } = column;
  if (typeof title === 'function') return null;
  return title;
};

/**
 * Number of empty cells needed to complete the last row.
 * The design draws borders on the leftover slots too, so they must be rendered.
 */
export const getFillerCount = (
  visibleColumnCount: number,
  columnsPerRow: number
): number => {
  const remainder = visibleColumnCount % columnsPerRow;
  return remainder === 0 ? 0 : columnsPerRow - remainder;
};
